import type { MarketingProductListResponse } from '../../types';
import type {
  StyleHotProductCategoryProduct,
  StyleHotProductRawAdCategory,
  StyleHotProductRawCategoryTab,
} from './types';

export const STYLE_CATEGORY_PRODUCT_PAGE_SIZE = 50;

export function getDefaultStyleCategoryTabIndex(
  tabs?: StyleHotProductRawAdCategory['tabs'],
): number {
  return Array.isArray(tabs) && tabs.length > 2 ? 2 : 0;
}

export function getInitialStyleCategoryTabIndex(
  styleCategory?: StyleHotProductRawAdCategory | null,
): number {
  const tabs = styleCategory?.tabs;
  const rawActiveTabIndex = styleCategory?.active_tab_index;
  const activeTabIndex =
    rawActiveTabIndex === null || rawActiveTabIndex === undefined || rawActiveTabIndex === ''
      ? NaN
      : Number(rawActiveTabIndex);

  if (
    Array.isArray(tabs) &&
    Number.isInteger(activeTabIndex) &&
    activeTabIndex >= 0 &&
    activeTabIndex < tabs.length
  ) {
    return activeTabIndex;
  }

  return getDefaultStyleCategoryTabIndex(tabs);
}

export function getMarketingProductListItems(
  response?: MarketingProductListResponse | null,
): StyleHotProductCategoryProduct[] {
  if (Array.isArray(response?.items)) {
    return response.items as StyleHotProductCategoryProduct[];
  }

  if (Array.isArray(response?.list)) {
    return response.list as StyleHotProductCategoryProduct[];
  }

  return [];
}

export function hasLoadedStyleCategoryProducts(tab?: StyleHotProductRawCategoryTab): boolean {
  return Boolean(
    tab?.products?.some(
      (product) =>
        product?.shop_sku ||
        product?.name ||
        product?.short_name ||
        product?.image ||
        product?.correlation,
    ),
  );
}

export function mergeStyleCategoryTabProducts(
  tab: StyleHotProductRawCategoryTab,
  products: StyleHotProductCategoryProduct[],
): StyleHotProductRawCategoryTab {
  const configuredProducts = Array.isArray(tab.products) ? tab.products : [];
  const configById = new Map(
    configuredProducts
      .filter((product) => product?.id)
      .map((product) => [Number(product.id), product]),
  );

  return {
    ...tab,
    products: products.map((product, index) => {
      const configuredProduct = configById.get(Number(product.id)) || configuredProducts[index];

      return configuredProduct ? { ...product, ...configuredProduct } : product;
    }),
  };
}

export function mergeStyleCategoryProductsByTabIndex(
  styleCategory: StyleHotProductRawAdCategory | null | undefined,
  tabIndex: number,
  products: StyleHotProductCategoryProduct[],
): StyleHotProductRawAdCategory | null | undefined {
  const tabs = styleCategory?.tabs;

  if (!Array.isArray(tabs) || !tabs[tabIndex]) {
    return styleCategory;
  }

  return {
    ...styleCategory,
    tabs: tabs.map((tab, index) =>
      index === tabIndex ? mergeStyleCategoryTabProducts(tab, products) : tab,
    ),
  };
}
